import { useCallback } from 'react'

/**
 * Hook for creating a toggle handler that switches between two actions based on a check function.
 * Common use case: toggle visibility (hide/unhide), toggle list membership (add/remove), etc.
 *
 * @example
 * ```tsx
 * const handleToggleHide = useToggleHandler(
 *   (id) => hiddenItems.includes(id),
 *   unhide.mutate,
 *   hide.mutate
 * )
 * ```
 */
export function useToggleHandler<TId>(
  checkFn: (id: TId) => boolean,
  actionWhenTrue: (id: TId) => void,
  actionWhenFalse: (id: TId) => void,
) {
  return useCallback(
    (id: TId) => {
      if (checkFn(id)) {
        actionWhenTrue(id)
      } else {
        actionWhenFalse(id)
      }
    },
    [checkFn, actionWhenTrue, actionWhenFalse],
  )
}
